import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { hasValue } from '@/utils/legacy-utils';

// ============================================================================
// External Declarations
// ============================================================================

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const isoDatetimeToFormattedDatetime = _wg('isoDatetimeToFormattedDatetime');

// ============================================================================
// Types
// ============================================================================

interface CallCardCommentsProps {
  callCardId?: number | null;
  comments?: any[];
  canSend?: boolean;
  canUpdate?: boolean;
  isEditingClosedCallCard?: boolean;
  onCommentAddedToNew?: (comment: { id: number; entry: string; dateCreated: string; creatorName: string }) => void;
  onCommentUpdated?: (data: { id: number; entry: string }) => void;
  onEditStarted?: () => void;
  onEditCancelled?: () => void;
  onDisabledFieldClick?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function CallCardComments({
  callCardId = null,
  comments = [],
  canSend = false,
  canUpdate = false,
  isEditingClosedCallCard = false,
  onCommentAddedToNew,
  onCommentUpdated,
  onEditStarted,
  onEditCancelled,
  onDisabledFieldClick,
}: CallCardCommentsProps) {
  const { theme } = useTheme();

  const [isMobile, setIsMobile] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [isSending, setIsSending] = useState(false);
  // Revision counter — incremented when external code mutates the comments
  // array in place (PubNub realtime).  Forces useMemo to recompute.
  const [commentsRevision, setCommentsRevision] = useState(0);
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const newCommentInputRef = useRef<HTMLTextAreaElement>(null);
  const editCommentInputRef = useRef<HTMLTextAreaElement>(null);

  const commentCount = useMemo(() => {
    return comments?.length || 0;
  }, [comments, commentsRevision]);

  const sortedComments = useMemo((): any[] => {
    if (!comments || comments.length === 0) return [];
    return [...comments].sort((a, b) => {
      const dateA = new Date(a.dateCreated).getTime();
      const dateB = new Date(b.dateCreated).getTime();
      if (dateA === dateB) {
        return a.id - b.id;
      }
      return dateA - dateB;
    });
  }, [comments, commentsRevision]);

  // Mobile check (debounced to prevent re-render thrashing)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 768), 150);
    };
    setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const formatDateTime = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    return isoDatetimeToFormattedDatetime(dateStr, (window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT);
  }, []);

  const scrollToBottom = useCallback(() => {
    // Double requestAnimationFrame ensures DOM is fully rendered before scrolling
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const commentsDiv = commentsContainerRef.current;
        if (commentsDiv) {
          commentsDiv.scrollTop = commentsDiv.scrollHeight;
        }
      });
    });
  }, []);

  // Scroll to bottom on mount
  useEffect(() => {
    scrollToBottom();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch comments for changes and scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [comments, scrollToBottom]);

  // Register component for realtime updates
  useEffect(() => {
    const componentRef = {
      handleRealtimeAdd: (message: any) => {
        // The parent mutates the comments array in place — bump revision
        // so useMemo recomputes and the new comment renders.
        setCommentsRevision(r => r + 1);
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      },
      handleRealtimeUpdate: (message: any) => {
        // Bump revision so the updated entry text re-renders
        setCommentsRevision(r => r + 1);
        // If we're editing this comment, update our edit text
        setEditingCommentId(prevId => {
          if (prevId === message.id) {
            setEditingCommentText(message.entry);
          }
          return prevId;
        });
      },
      refresh: () => {
        setCommentsRevision(r => r + 1);
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      },
    };

    (window as any).CallCardCommentsComponent = componentRef;
    return () => {
      if ((window as any).CallCardCommentsComponent === componentRef) {
        (window as any).CallCardCommentsComponent = null;
      }
    };
  }, [scrollToBottom]);

  const addComment = useCallback(() => {
    if (!newCommentText.trim() || isSending) return;

    setIsSending(true);
    const commentText = newCommentText.trim();
    const currentCCId = hasValue(callCardId) ? callCardId : null;
    const saveOnClosedCall = isEditingClosedCallCard;

    api.saveCallComment(currentCCId, commentText, saveOnClosedCall, (result: any) => {
      try {
        // If this is a new call card (no ID yet), emit the comment to attach
        if (currentCCId === null && result) {
          onCommentAddedToNew?.({
            id: result.id,
            entry: commentText,
            dateCreated: result.dateCreated,
            creatorName: result.creatorName,
          });
        }
      } finally {
        setNewCommentText('');
        setIsSending(false);
        setCommentsRevision(r => r + 1);
        // Focus textarea and reset height
        requestAnimationFrame(() => {
          const textarea = newCommentInputRef.current;
          if (textarea) {
            textarea.focus();
            textarea.style.height = 'auto';
          }
        });
      }
    }, (error: any) => {
      App.showPopupNotification("Call note failed to save.", "error", 30);
      setIsSending(false);
    });
  }, [newCommentText, isSending, callCardId, isEditingClosedCallCard, onCommentAddedToNew]);

  const startEdit = useCallback((comment: any) => {
    setIsEditing(true);
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.entry);
    onEditStarted?.();
    // Focus the edit textarea after React renders the edit panel
    requestAnimationFrame(() => {
      const textarea = editCommentInputRef.current;
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
    });
  }, [onEditStarted]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditingCommentId(null);
    setEditingCommentText('');
    onEditCancelled?.();
  }, [onEditCancelled]);

  const saveEdit = useCallback(() => {
    if (!editingCommentText.trim() || !editingCommentId) return;

    const commentId = editingCommentId;
    const updatedEntry = editingCommentText.trim();
    const updateOnClosedCall = isEditingClosedCallCard;

    api.updateCallComment(commentId, updatedEntry, updateOnClosedCall, (result: any) => {
      onCommentUpdated?.({ id: commentId, entry: updatedEntry });
      cancelEdit();
    }, (error: any) => {
      App.showPopupNotification("Failed to update comment.", "error", 30);
    });
  }, [editingCommentText, editingCommentId, isEditingClosedCallCard, onCommentUpdated, cancelEdit]);

  const handleTextareaInput = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = event.target;
    setNewCommentText(textarea.value);
    // Auto-resize textarea
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }, []);

  const handleKeydown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter without shift sends the comment
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      addComment();
    }
  }, [addComment]);

  const handleDisabledClick = useCallback(() => {
    onDisabledFieldClick?.();
  }, [onDisabledFieldClick]);

  return (
    <div className={`callcard-comments ${theme}`}>
      {/* Edit Mode */}
      {isEditing ? (
        <div className="edit-panel">
          <div className="edit-container">
            <div className="edit-header">Edit Comment</div>
            <div className="edit-body">
              <textarea
                ref={editCommentInputRef}
                value={editingCommentText}
                onChange={(e) => setEditingCommentText(e.target.value)}
                className="edit-textarea"
                rows={6}
                inputMode="text"
                autoComplete="off"
                placeholder="Edit your comment..."
              />
              <div className="edit-actions">
                <button type="button" className="btn-cancel" onClick={cancelEdit}>Cancel</button>
                <button type="button" className="btn-save" onClick={saveEdit}>Update</button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Normal View */
        <div className="comments-panel">
          <div className="comments-header">
            <span>Comments</span>
            {commentCount > 0 && (
              <span className="comment-count">({commentCount})</span>
            )}
          </div>

          <div className="comments-body">
            <div ref={commentsContainerRef} className="comments-container">
              {/* Empty State */}
              {commentCount === 0 && (
                <div className="empty-state">
                  No comments yet.
                </div>
              )}

              {/* Comments List */}
              {sortedComments.map((comment: any) => (
                <div key={comment.id} className="comment-item">
                  <div className="comment-header">
                    <span className="comment-author">{comment.creatorName}</span>
                    <span className="comment-date">{formatDateTime(comment.dateCreated)}</span>
                    {canUpdate && canSend && (
                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() => startEdit(comment)}
                        title="Edit comment"
                      >
                        <i className="fa fa-pencil"></i>
                      </button>
                    )}
                  </div>
                  <div className="comment-text">{comment.entry}</div>
                </div>
              ))}
            </div>

            {/* Input Area */}
            <div className="comment-input-area">
              {/* Transparent overlay to capture clicks when disabled */}
              {!canSend && (
                <div className="disabled-overlay" onClick={handleDisabledClick}></div>
              )}
              <textarea
                ref={newCommentInputRef}
                value={newCommentText}
                onChange={handleTextareaInput}
                onKeyDown={handleKeydown}
                className="comment-input"
                rows={1}
                maxLength={5000}
                inputMode="text"
                autoComplete="off"
                placeholder="New call comment"
                disabled={isSending || !canSend}
              />
              <button
                type="button"
                className="btn-send"
                onClick={addComment}
                disabled={isSending || !canSend || !newCommentText.trim()}
              >
                <i className="fa fa-paper-plane"></i>
                <span>Send</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

(window as any).CallCardCommentsReact = CallCardComments;
